/**
 * 全局黑板服务
 *
 * 提供所有行为树共享的全局变量存储
 *
 * 使用方式：
 * ```typescript
 * // 注册服务（在 BehaviorTreePlugin.install 中自动完成）
 * core.services.registerSingleton(GlobalBlackboardService);
 *
 * // 获取服务
 * const blackboard = core.services.resolve(GlobalBlackboardService);
 * ```
 */
export class GlobalBlackboardService {
    constructor() {
        this.variables = new Map();
    }
    dispose() {
        this.variables.clear();
    }
    /**
     * 定义全局变量
     */
    defineVariable(name, type, initialValue, options) {
        const variable = {
            name,
            type,
            value: initialValue,
            readonly: options?.readonly ?? false
        };
        if (options?.description !== undefined) {
            variable.description = options.description;
        }
        this.variables.set(name, variable);
    }
    /**
     * 获取全局变量值
     */
    getValue(name) {
        const variable = this.variables.get(name);
        return variable?.value;
    }
    /**
     * 设置全局变量值
     */
    setValue(name, value, force = false) {
        const variable = this.variables.get(name);
        if (!variable) {
            return false;
        }
        if (variable.readonly && !force) {
            return false;
        }
        variable.value = value;
        return true;
    }
    /**
     * 检查全局变量是否存在
     */
    hasVariable(name) {
        return this.variables.has(name);
    }
    /**
     * 删除全局变量
     */
    removeVariable(name) {
        return this.variables.delete(name);
    }
    /**
     * 获取所有变量名
     */
    getVariableNames() {
        return Array.from(this.variables.keys());
    }
    /**
     * 获取所有变量
     */
    getAllVariables() {
        return Array.from(this.variables.values());
    }
    /**
     * 清空所有全局变量
     */
    clear() {
        this.variables.clear();
    }
    /**
     * 批量设置变量
     */
    setVariables(values) {
        for (const [name, value] of Object.entries(values)) {
            const variable = this.variables.get(name);
            if (variable && !variable.readonly) {
                variable.value = value;
            }
        }
    }
    /**
     * 批量获取变量
     */
    getVariables(names) {
        const result = {};
        for (const name of names) {
            const value = this.getValue(name);
            if (value !== undefined) {
                result[name] = value;
            }
        }
        return result;
    }
    /**
     * 导出配置
     */
    exportConfig() {
        return {
            version: '1.0',
            variables: Array.from(this.variables.values())
        };
    }
    /**
     * 导入配置
     */
    importConfig(config) {
        this.variables.clear();
        for (const variable of config.variables) {
            this.variables.set(variable.name, variable);
        }
    }
    /**
     * 序列化为 JSON
     */
    toJSON() {
        return JSON.stringify(this.exportConfig(), null, 2);
    }
    /**
     * 从 JSON 反序列化
     */
    static fromJSON(json) {
        return JSON.parse(json);
    }
}
//# sourceMappingURL=GlobalBlackboardService.js.map